import {Rect} from "../../utils/Rect";
import {fetchImage} from "../../utils/fetch";
import {FlyerController, FlyerControllerState} from "./FlyerController";
import {Sample} from "./InputSampler";
import {Animation, timeline} from "../../utils/Animation";

import {inject} from "../../services/di";
import {ConfigService, RuntimeConfig} from "../../services/ConfigService";
import {SLItemClippingDetails} from "../../services/ShoppingListService";

import {Flyer, FlyerItem} from "../../definitions/FlyerDefinitions";
import {FlyerItemCoupon} from "../../definitions/CouponDefinitions";
import {Clipping, ClippingType} from "../../definitions/CommonDefinitions";


const COUPON_BADGE_SIZE = 120;
const COUPON_BADGE_MAX = 140;

// Compile a `type` shader with source `src` for the given WebGL context.
function compileShader(gl: WebGLRenderingContext, type: number, src: string) {
	const shader = gl.createShader(type);
	gl.shaderSource(shader, src);
	gl.compileShader(shader);
	if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
		console.error(gl.getShaderInfoLog(shader));
	return shader;
}

// Link a program with `shaders` and vertex attribute bindings `binds`.
function linkProgram(
	gl: WebGLRenderingContext, shaders: WebGLShader[], binds: { [index: string]: number }) {
	const program = gl.createProgram();
	for (let i = 0, n = shaders.length; i < n; ++i)
		gl.attachShader(program, shaders[i]);
	for (const k in binds)
		if (binds.hasOwnProperty(k))
			gl.bindAttribLocation(program, binds[k], k);

	gl.linkProgram(program);
	if (!gl.getProgramParameter(program, gl.LINK_STATUS))
		console.error(gl.getProgramInfoLog(program));

	return program;
}

function preferredResolution(tileWidth: number, tileHeight: number, zoomScale: number, resolutions: number[]) {
	const maxTile = Math.max(tileWidth, tileHeight);
	const goalTile = maxTile * zoomScale;
	let minDelta = Number.POSITIVE_INFINITY;
	let bestResolution = 0;
	for (let i = 0, j = 0, n = resolutions.length; j < n; ++j) {
		const delta = Math.abs(resolutions[j] * maxTile - goalTile);
		if (delta < minDelta) {
			bestResolution = i;
			minDelta = delta;
		}
		++i;
	}
	return bestResolution;
}

// Calls `fn` for each tile of resolution `resolutionIndex` needed to
// render, the `contentRect` of flyer of size
// `contentWidth` by `contentHeight` with available resolutions
// `tileResolutions`.
function forEachTile(
	tileResolutions: number[],
	resolutionIndex: number,
	contentLeft: number,
	contentTop: number,
	contentWidth: number,
	contentHeight: number,
	contentRect: Rect,
	tileContentOverlap: number,
	tileContentSize: number,
	fn: (z: number, x: number, y: number, maxY: number,
				left: number, top: number,
				width: number, height: number,
				srcLeft: number, srcTop: number,
				srcWidth: number, srcHeight: number) => void) {
	const contentRight = contentLeft + contentWidth;
	const tileSize = tileContentSize * tileResolutions[resolutionIndex];
	const totalCols = Math.ceil((contentLeft + contentWidth) / tileSize);
	const totalRows = Math.ceil((contentTop + contentHeight) / tileSize);
	const topOffset = Math.ceil(totalRows * tileSize - contentHeight);

	const tilesLeft = Math.max(0, Math.floor(contentRect.left / tileSize));
	const tilesTop = Math.max(0, Math.floor(contentRect.top / tileSize));
	const tilesRight =
			Math.min(totalCols - 1, Math.floor(contentRect.right / tileSize));
	const tilesBottom =
			Math.min(totalRows - 1,
							Math.floor((contentRect.bottom + topOffset) / tileSize));
	const tileImageSize = tileContentSize + tileContentOverlap * 2;

	for (let y = tilesTop; y <= tilesBottom; y++) {
		for (let x = tilesLeft; x <= tilesRight; x++) {
			const unclippedX = x * tileSize - contentLeft;
			const unclippedY = y * tileSize - contentTop;
			const clippedX = Math.max(0, unclippedX);
			const clippedY = Math.max(0, unclippedY);
			const clippedWidth =
					Math.min(tileSize + Math.min(0, unclippedX),
									contentRight - x * tileSize + Math.min(0, unclippedX));
			const srcX = tileContentOverlap +
					(unclippedX < 0 ? -unclippedX / tileSize * tileContentSize : 0);
			const srcY = tileContentOverlap +
					(unclippedY < 0 ? -unclippedY / tileSize * tileContentSize : 0);
			const srcWidth = unclippedX < 0 ?
					Math.min(tileImageSize - tileContentOverlap - srcX,
									(contentRight - x * tileSize + unclippedX) / tileSize * tileContentSize) :
					Math.min(tileSize, contentRight - x * tileSize) /
							tileSize * tileContentSize;
			fn(resolutionIndex, x, y, totalRows,
				clippedX, clippedY - topOffset, clippedWidth, tileSize,
				srcX, srcY, srcWidth, tileContentSize);
		}
	}
}

class TextureLoader {
	protected _imageCache: { [key: string]: HTMLImageElement; } = {};
	protected _imageLoading: { [key: string]: boolean; } = {};

	onload: (loader: TextureLoader) => void;

	get(base: string, z: number, x: number, y: number, maxY: number, walk: boolean, maxZ: number, tileContentSize: number, suffix: string) {
		let img = this._imageCache[this._path(base, z, x, y, suffix, maxY)];
		let scale = 1;
		let invScale = 1;
		let bias_x = 0;
		let bias_y = 0;
		while (walk && !img && ++z < maxZ) {
			scale *= 0.5;
			invScale *= 2;
			bias_x = (x % invScale) / invScale * tileContentSize;
			bias_y = (y % invScale) / invScale * tileContentSize;
			img = this._imageCache[this._path(base, z, Math.floor(x * scale),
																				Math.floor(y * scale), suffix, maxY)];
		}

		return {
			img: img,
			scale: scale,
			bias_x: bias_x,
			bias_y: bias_y
		};
	}

	load(base: string, z: number, x: number, y: number, maxY: number, suffix: string) {
		const path = this._path(base, z, x, y, suffix, maxY);
		if (this._imageLoading[path])
			return;

		this._imageLoading[path] = true;
		fetchImage(path, true)
				.then((img) => {
					this._imageCache[path] = img;
					delete this._imageLoading[path];
					if (this.onload)
						this.onload(this);
				});
	}

	private _path(base: string, z: number, x: number, y: number, suffix: string, maxY: number) {
		return `${base}${z}_${x}_${maxY - y - 1}.${suffix}`;
	}
}

// A shader program that applies a single texture.
class TextureProgram {
	program: WebGLProgram;
	mvpLocation: WebGLUniformLocation;
	textureLocation: WebGLUniformLocation;

	constructor(private _gl: WebGLRenderingContext) {
		const gl = this._gl;
		const vertexShader = compileShader(gl, gl.VERTEX_SHADER, `
#version 100

attribute vec3 position;
attribute vec2 texCoord0;
uniform mat4 mvp;
varying vec2 vTexCoord0;
void main() {
gl_Position = mvp * vec4(position, 1);
vTexCoord0 = texCoord0;
}`);
		const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, `
#version 100

precision highp float;
uniform sampler2D texture;
varying vec2 vTexCoord0;

void main() {
gl_FragColor = texture2D(texture, vTexCoord0);
}`);
		const program = linkProgram(gl, [vertexShader, fragmentShader], {
			position: 0,
			texCoord0: 1
		});

		this.textureLocation = gl.getUniformLocation(program, "texture");
		this.mvpLocation = gl.getUniformLocation(program, "mvp");
		this.program = program;

		gl.deleteShader(vertexShader);
		gl.deleteShader(fragmentShader);
	}

	dispose() {
		const gl = this._gl;
		if (!gl)
			return;

		gl.deleteProgram(this.program);
		this.program = null;
		this._gl = null;
	}
}

// A shader program that fills with a single color.
class BorderProgram {
	program: WebGLProgram;
	mvpLocation: WebGLUniformLocation;
	colorLocation: WebGLUniformLocation;
	borderColorLocation: WebGLUniformLocation;

	constructor(private _gl: WebGLRenderingContext) {
		const gl = this._gl;
		const vertexShader = compileShader(gl, gl.VERTEX_SHADER, `
#version 100

attribute vec3 position;
attribute vec2 texCoord0;
varying vec2 vTexCoord0;
uniform mat4 mvp;

void main() {
gl_Position = mvp * vec4(position, 1);
vTexCoord0 = texCoord0;
}`);
		const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, `
#version 100

precision highp float;
varying vec2 vTexCoord0;
uniform vec4 color;
uniform vec4 borderColor;

void main() {
if (vTexCoord0.s < 0.02 || vTexCoord0.s > 0.98 ||
		vTexCoord0.t < 0.02 || vTexCoord0.t > 0.98)
	gl_FragColor = borderColor;
else
	gl_FragColor = color;
}`);

		const program = linkProgram(gl, [vertexShader, fragmentShader], {
			position: 0,
			texCoord0: 1
		});

		this.mvpLocation = gl.getUniformLocation(program, "mvp");
		this.colorLocation = gl.getUniformLocation(program, "color");
		this.borderColorLocation = gl.getUniformLocation(program, "borderColor");
		this.program = program;

		gl.deleteShader(vertexShader);
		gl.deleteShader(fragmentShader);
	}

	dispose() {
		const gl = this._gl;
		if (!gl)
			return;

		gl.deleteProgram(this.program);
		this.program = null;
		this._gl = null;
	}
}

// A shader program that fills with a single color.
class AttributeColorProgram {
	program: WebGLProgram;
	mvpLocation: WebGLUniformLocation;

	constructor(private _gl: WebGLRenderingContext) {
		const gl = this._gl;
		const vertexShader = compileShader(gl, gl.VERTEX_SHADER, `
#version 100

attribute vec3 position;
attribute vec4 color;
varying vec4 vColor;
uniform mat4 mvp;
void main() {
gl_Position = mvp * vec4(position, 1);
vColor = color;
}`);
		const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, `
#version 100

precision highp float;
varying vec4 vColor;
void main() {
gl_FragColor = vColor;
}`);

		const program = linkProgram(gl, [vertexShader, fragmentShader], {
			position: 0,
			color: 1
		});

		this.mvpLocation = gl.getUniformLocation(program, "mvp");
		this.program = program;

		gl.deleteShader(vertexShader);
		gl.deleteShader(fragmentShader);
	}

	dispose() {
		const gl = this._gl;
		if (!gl)
			return;

		gl.deleteProgram(this.program);
		this.program = null;
		this._gl = null;
	}
}

// A shader program that fills with a single color.
class ColorProgram {
	program: WebGLProgram;
	mvpLocation: WebGLUniformLocation;
	colorLocation: WebGLUniformLocation;

	constructor(private _gl: WebGLRenderingContext) {
		const gl = this._gl;
		const vertexShader = compileShader(gl, gl.VERTEX_SHADER, `
#version 100

attribute vec3 position;
uniform mat4 mvp;
void main() {
gl_Position = mvp * vec4(position, 1);
}`);
		const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, `
#version 100

precision highp float;
uniform vec4 color;
void main() {
gl_FragColor = color;
}`);

		const program = linkProgram(gl, [vertexShader, fragmentShader], {
			position: 0
		});

		this.colorLocation = gl.getUniformLocation(program, "color");
		this.mvpLocation = gl.getUniformLocation(program, "mvp");
		this.program = program;

		gl.deleteShader(vertexShader);
		gl.deleteShader(fragmentShader);
	}

	dispose() {
		const gl = this._gl;
		if (!gl)
			return;

		gl.deleteProgram(this.program);
		this.program = null;
		this._gl = null;
	}
}

class TexturedQuadVertexBuffer {
	private _buffer: WebGLBuffer;

	constructor(private _gl: WebGLRenderingContext) {
		const gl = this._gl;
		const buffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

		const u0 = 0;
		const v0 = 0;
		const u1 = 1;
		const v1 = 1;
		const z = -1;
		const data = new Float32Array([
			0, 0, z,
			u0, v1,

			0, 1, z,
			u0, v0,

			1, 0, z,
			u1, v1,

			1, 0, z,
			u1, v1,

			0, 1, z,
			u0, v0,

			1, 1, z,
			u1, v0
		]);

		gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
		this._buffer = buffer;
	}

	dispose() {
		const gl = this._gl;
		if (!gl)
			return;

		gl.deleteBuffer(this._buffer);
		this._buffer = null;
		this._gl = null;
	}

	bind() {
		const gl = this._gl;
		gl.bindBuffer(gl.ARRAY_BUFFER, this._buffer);
		gl.enableVertexAttribArray(0);
		gl.enableVertexAttribArray(1);
		gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 20, 0);
		gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 20, 12);
	}
}

class TileAtlasSlot {
	x: number;
	y: number;
	u0: number;
	v0: number;
	o: number;
	maxSize: number;
	lastUsed: number;
	frame: number;
}

class TileAtlas {
	texture: WebGLTexture;
	frame: number = 0;
	lodBias: number = 0;

	onoversubscribed: () => void;
	onuploadlimit: () => void;

	private _freeSlots: TileAtlasSlot[] = [];
	private _images: { [key: string]: TileAtlasSlot } = {};

	constructor(private _gl: WebGLRenderingContext, tileSize: number) {
		const gl = this._gl;

		// FIXME [FW-68] Intel GPUs have a performance cliff past 2048x2048
		// textures, despite advertising support for higher.
		const maxSize = 4096;

		const numRows = maxSize / tileSize;
		const numCols = maxSize / tileSize;
		const now = flipp.now();
		for (let j = 0, y = 0; j < numRows; ++j, y += tileSize) {
			for (let i = 0, x = maxSize - tileSize; i < numCols; ++i,
					x -= tileSize) {
				this._freeSlots.push({
					u0: x / maxSize,
					v0: y / maxSize,
					x: x,
					y: y,
					o: 1,
					maxSize: maxSize,
					lastUsed: now,
					frame: this.frame
				});
			}
		}

		const texture = gl.createTexture();
		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, maxSize, maxSize, 0,
									gl.RGBA, gl.UNSIGNED_BYTE, null);

		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
		this.texture = texture;
	}

	dispose() {
		const gl = this._gl;
		if (!gl)
			return;

		gl.deleteTexture(this.texture);
		this.texture = null;
		this._gl = null;
	}

	touch(img: HTMLImageElement) {
		const path = img.src;
		const slot = this._images[path];
		if (slot) {
			slot.frame = this.frame;
			return slot;
		}
	}

	get occupancy(): number {
		let n = 0;
		let m = 0;
		for (const k in this._images) {
			if (!this._images.hasOwnProperty(k))
				continue;

			const image = this._images[k];
			if (image.frame === this.frame)
				++n;
			++m;
		}
		return n / m;
	}

	getTile(
		upload: { uploadCount: number },
		img: HTMLImageElement,
		resolution: number,
		left: number,
		top: number,
		width: number,
		height: number,
		visibleRect: Rect,
		resolutions: number[]): TileAtlasSlot | number {
		const path = img.src;
		let slot = this._images[path];
		if (slot) {
			slot.frame = this.frame;
			return slot;
		}

		if (upload.uploadCount > 100) {
			if (this.onuploadlimit)
				this.onuploadlimit();
			return null;
		}

		const gl = this._gl;
		slot = this._freeSlots.pop();
		if (!slot) {
			let victimKey: string = null;
			for (const k in this._images) {
				if (!this._images.hasOwnProperty(k))
					continue;

				const image = this._images[k];
				if (image.frame === this.frame)
					continue;

				victimKey = k;
				break;
			}
			if (victimKey === null) {
				if (this.onoversubscribed)
					this.onoversubscribed();
				return null;
			}

			const victim = this._images[victimKey];
			if (victim.frame === this.frame) {
				if (this.onoversubscribed)
					this.onoversubscribed();
				return null;
			}

			delete this._images[victimKey];
			slot = victim;
			if (!slot) {
				if (this.onoversubscribed)
					this.onoversubscribed();
				return null;
			}
		}

		if (upload.uploadCount !== -1)
			++upload.uploadCount;

		gl.bindTexture(gl.TEXTURE_2D, this.texture);

		gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
		gl.texSubImage2D(gl.TEXTURE_2D, 0,
				slot.x, slot.y, gl.RGBA, gl.UNSIGNED_BYTE, img);

		slot.o = 1;
		slot.lastUsed = flipp.now();
		slot.frame = this.frame;
		this._images[path] = slot;

		return slot;
	}
}

class Float32Vector {
	buffer: Float32Array;
	fill: number;

	constructor() {
		this.fill = 0;
		this.buffer = new Float32Array(30 * 256);
	}

	push(v: number) {
		if (this.fill === this.buffer.length)
			this._grow();

		this.buffer[this.fill++] = v;
	}

	clear() {
		this.fill = 0;
	}

	private _grow() {
		const data = this.buffer;
		this.buffer = new Float32Array(data.length * 2);
		this.buffer.set(data, 0);
		return this.buffer;
	}
}

class TileBatch {
	buffer: Float32Array;
	bufferFill: number;
	vertexBuffer: WebGLBuffer;
	atlas: TileAtlas;

	constructor(private _gl: WebGLRenderingContext, tileSize: number) {
		this.atlas = new TileAtlas(this._gl, tileSize);
		this.vertexBuffer = this._gl.createBuffer();
		this.bufferFill = 0;
		this.buffer = new Float32Array(30 * 256);
	}

	dispose() {
		this.atlas.dispose();
		this._gl.deleteBuffer(this.vertexBuffer);
	}

	grow() {
		const data = this.buffer;
		this.buffer = new Float32Array(data.length * 2);
		this.buffer.set(data, 0);
		return this.buffer;
	}

	reset() {
		this.bufferFill = 0;
	}
}

class SceneNode {
	protected _gl: WebGLRenderingContext;
	modelMatrix: mat4 = mat4.create();
	scene: Scene;
	tag: string;
	_children: SceneNode[] = [];

	get children(): SceneNode[] {
		return this._children;
	}

	set children(value: SceneNode[]) {
		this._children = value;
		if (this._gl) {
			for (let i = 0, n = (this._children || []).length; i < n; ++i) {
				const node = this._children[i];
				node.handleContextRestored(this._gl);
			}
		}
	}

	handleContextRestored(gl: WebGLRenderingContext) {
		this._gl = gl;
		this._handleContextRestored();

		for (let i = 0, n = this.children.length; i < n; ++i) {
			const node = this.children[i];
			node.handleContextRestored(this._gl);
		}
	}

	predraw(camera: CameraNode, viewPerspective: mat4, transform: mat4) {
		this._predraw(camera, viewPerspective, transform);

		for (let i = 0, n = this.children.length; i < n; ++i) {
			const t = transform ?
					mat4.mul(mat4.create(), this.modelMatrix, transform) :
					this.modelMatrix;
			this.children[i].predraw(camera, viewPerspective, t);
		}
	}

	draw(camera: CameraNode, viewPerspective: mat4, transform: mat4) {
		this._draw(camera, viewPerspective, transform);

		for (let i = 0, n = this.children.length; i < n; ++i) {
			const t = transform ?
					mat4.mul(mat4.create(), this.modelMatrix, transform) :
					this.modelMatrix;
			this.children[i].draw(camera, viewPerspective, t);
		}
	}

	protected _handleContextRestored() {}

	protected _draw(camera: CameraNode, viewPerspective: mat4, transform: mat4) {}

	protected _predraw(camera: CameraNode, viewPerspective: mat4, transform: mat4) {}
}

class CameraNode extends SceneNode {
	modelMatrix: mat4 = mat4.create();
	invModelMatrix: mat4 = mat4.create();
	projectionMatrix: mat4 = mat4.create();
	viewportWidth: number;
	viewportHeight: number;

	protected _mm = mat4.create();
	protected _origin = vec4.fromValues(0, 0, 0, 1);

	protected _eye = vec4.fromValues(0, 0, 0, 1);
	protected _topLeft = vec4.fromValues(-1, 1, -1, 1);
	protected _topRight = vec4.fromValues(1, -1, -1, 1);
	protected _bottomLeft = vec4.fromValues(-1, -1, -1, 1);
	protected _bottomRight = vec4.fromValues(1, -1, -1, 1);

	protected _draw(camera: CameraNode, viewPerspective: mat4, transform: mat4) {
		if (this === camera)
			return;

		const near = 1;
		const far = 4000000000;

		// The quad is transformed, clipped and projected in software to
		// determine screen coverage for accurate LOD tesselation and
		// texture selection.
		const mm = transform ?
				mat4.mul(this._mm, transform, this.modelMatrix) :
				mat4.copy(this._mm, this.modelMatrix);

		const eye = vec4.transformMat4(this._eye, this._origin, mm);

		const fov = Math.PI / 2;
		const aspect = this.viewportWidth / this.viewportHeight;

		const nearHeight = near * Math.tan(fov / 2);
		const nearWidth = aspect * nearHeight;
		const farHeight = far * Math.tan(fov / 2);
		const farWidth = aspect * farHeight;

		let topLeftNear = vec4.fromValues(-nearWidth, nearHeight, -near, 1);
		let topRightNear = vec4.fromValues(nearWidth, nearHeight, -near, 1);
		let bottomLeftNear = vec4.fromValues(-nearWidth, -nearHeight, -near, 1);
		let bottomRightNear = vec4.fromValues(nearWidth, -nearHeight, -near, 1);

		topLeftNear = vec4.transformMat4(topLeftNear, topLeftNear, mm);
		topRightNear = vec4.transformMat4(topRightNear, topRightNear, mm);
		bottomLeftNear = vec4.transformMat4(bottomLeftNear, bottomLeftNear, mm);
		bottomRightNear = vec4.transformMat4(bottomRightNear, bottomRightNear, mm);

		let topLeftFar = vec4.fromValues(-farWidth, farHeight, -far, 1);
		let topRightFar = vec4.fromValues(farWidth, farHeight, -far, 1);
		let bottomLeftFar = vec4.fromValues(-farWidth, -farHeight, -far, 1);
		let bottomRightFar = vec4.fromValues(farWidth, -farHeight, -far, 1);

		topLeftFar = vec4.transformMat4(topLeftFar, topLeftFar, mm);
		topRightFar = vec4.transformMat4(topRightFar, topRightFar, mm);
		bottomLeftFar = vec4.transformMat4(bottomLeftFar, bottomLeftFar, mm);
		bottomRightFar = vec4.transformMat4(bottomRightFar, bottomRightFar, mm);

		const linesBuffer = this.scene.linesBuffer;
		linesBuffer.push(eye[0]);
		linesBuffer.push(eye[1]);
		linesBuffer.push(eye[2]);
		linesBuffer.push(topLeftFar[0]);
		linesBuffer.push(topLeftFar[1]);
		linesBuffer.push(topLeftFar[2]);
		linesBuffer.push(eye[0]);
		linesBuffer.push(eye[1]);
		linesBuffer.push(eye[2]);
		linesBuffer.push(topRightFar[0]);
		linesBuffer.push(topRightFar[1]);
		linesBuffer.push(topRightFar[2]);
		linesBuffer.push(eye[0]);
		linesBuffer.push(eye[1]);
		linesBuffer.push(eye[2]);
		linesBuffer.push(bottomLeftFar[0]);
		linesBuffer.push(bottomLeftFar[1]);
		linesBuffer.push(bottomLeftFar[2]);
		linesBuffer.push(eye[0]);
		linesBuffer.push(eye[1]);
		linesBuffer.push(eye[2]);
		linesBuffer.push(bottomRightFar[0]);
		linesBuffer.push(bottomRightFar[1]);
		linesBuffer.push(bottomRightFar[2]);

		linesBuffer.push(topLeftNear[0]);
		linesBuffer.push(topLeftNear[1]);
		linesBuffer.push(topLeftNear[2]);
		linesBuffer.push(topRightNear[0]);
		linesBuffer.push(topRightNear[1]);
		linesBuffer.push(topRightNear[2]);
		linesBuffer.push(topRightNear[0]);
		linesBuffer.push(topRightNear[1]);
		linesBuffer.push(topRightNear[2]);
		linesBuffer.push(bottomRightNear[0]);
		linesBuffer.push(bottomRightNear[1]);
		linesBuffer.push(bottomRightNear[2]);
		linesBuffer.push(topLeftNear[0]);
		linesBuffer.push(topLeftNear[1]);
		linesBuffer.push(topLeftNear[2]);
		linesBuffer.push(bottomLeftNear[0]);
		linesBuffer.push(bottomLeftNear[1]);
		linesBuffer.push(bottomLeftNear[2]);
		linesBuffer.push(bottomLeftNear[0]);
		linesBuffer.push(bottomLeftNear[1]);
		linesBuffer.push(bottomLeftNear[2]);
		linesBuffer.push(bottomRightNear[0]);
		linesBuffer.push(bottomRightNear[1]);
		linesBuffer.push(bottomRightNear[2]);

		linesBuffer.push(topLeftFar[0]);
		linesBuffer.push(topLeftFar[1]);
		linesBuffer.push(topLeftFar[2]);
		linesBuffer.push(topRightFar[0]);
		linesBuffer.push(topRightFar[1]);
		linesBuffer.push(topRightFar[2]);
		linesBuffer.push(topRightFar[0]);
		linesBuffer.push(topRightFar[1]);
		linesBuffer.push(topRightFar[2]);
		linesBuffer.push(bottomRightFar[0]);
		linesBuffer.push(bottomRightFar[1]);
		linesBuffer.push(bottomRightFar[2]);
		linesBuffer.push(topLeftFar[0]);
		linesBuffer.push(topLeftFar[1]);
		linesBuffer.push(topLeftFar[2]);
		linesBuffer.push(bottomLeftFar[0]);
		linesBuffer.push(bottomLeftFar[1]);
		linesBuffer.push(bottomLeftFar[2]);
		linesBuffer.push(bottomLeftFar[0]);
		linesBuffer.push(bottomLeftFar[1]);
		linesBuffer.push(bottomLeftFar[2]);
		linesBuffer.push(bottomRightFar[0]);
		linesBuffer.push(bottomRightFar[1]);
		linesBuffer.push(bottomRightFar[2]);
	}
}

class TiledImageNode extends SceneNode {
	contentLeft: number;
	contentTop: number;
	contentWidth: number;
	contentHeight: number;
	resolutions: number[];
	scale: number;
	_src: string;
	_path: string;
	_overlap: number;
	_tileSize: number;
	_suffix: string;
	protected _mm = mat4.create();

	private _tileImageLoader: TextureLoader;

	private _topLeftContent = vec4.fromValues(0, 0, 0, 1);
	private _topRightContent = vec4.fromValues(0, 0, 0, 1);
	private _bottomLeftContent = vec4.fromValues(0, 0, 0, 1);
	private _bottomRightContent = vec4.fromValues(0, 0, 0, 1);

	// Temporaries used during _draw to reduce memory allocation.
	private _unitTopLeft = vec4.fromValues(0, 0, 0, 1);
	private _unitTopRight = vec4.fromValues(1, 0, 0, 1);
	private _unitBottomLeft = vec4.fromValues(0, 1, 0, 1);
	private _unitBottomRight = vec4.fromValues(1, 1, 0, 1);
	private _topLeft = vec4.create();
	private _topRight = vec4.create();
	private _bottomLeft = vec4.create();
	private _bottomRight = vec4.create();
	private _topLeftTexCoord = vec2.create();
	private _topRightTexCoord = vec2.create();
	private _bottomLeftTexCoord = vec2.create();
	private _bottomRightTexCoord = vec2.create();
	private _mvp = mat4.create();

	private _visibleRect = new Rect;
	private _loadRect = new Rect;

	set flyer(flyer: Flyer) {
		this.resolutions = flyer.resolutions;
		this._overlap = 0;
		this._path = "https://f.wishabi.net/" + flyer.path;
		this._tileSize = 256;
		this._suffix = "jpg";

		if (this.scene)
			this.scene.invalidate();
	}

	get tileImageLoader(): TextureLoader {
		return this._tileImageLoader;
	}

	set tileImageLoader(value: TextureLoader) {
		this._tileImageLoader = value;
		this._tileImageLoader.onload = () => {
			this.scene.invalidate();
		};
	}

	private static _intersect(
		p1: { coord: vec4; texCoord: vec2; },
		p2: { coord: vec4; texCoord: vec2; }, n: number, left: boolean) {
		const a = left ?
				(p1.coord[n] + p1.coord[3]) / (p1.coord[n] - p2.coord[n] + p1.coord[3] - p2.coord[3]) :
				(p1.coord[n] - p1.coord[3]) / (p1.coord[n] - p2.coord[n] + p2.coord[3] - p1.coord[3]);
		return {
			coord: vec4.lerp(vec4.create(), p1.coord, p2.coord, a),
			texCoord: vec2.lerp(vec2.create(), p1.texCoord, p2.texCoord, a)
		};
	}

	// http://en.wikipedia.org/wiki/Sutherland%E2%80%93Hodgman_algorithm
	private static _clip(points: { coord: vec4; texCoord: vec2; }[]) {
		let output = points.slice();
		for (let j = 0; j < 3; ++j) {
			let input = output.slice();
			output = [];
			let s = input[input.length - 1];
			for (let i = 0, n = input.length; i < n; ++i) {
				const e = input[i];
				if (e.coord[j] >= -e.coord[3]) {
					if (s.coord[j] < -s.coord[3]) {
						output.push(this._intersect(s, e, j, true));
					}
					output.push(e);
				} else if (s.coord[j] >= -s.coord[3]) {
					output.push(this._intersect(s, e, j, true));
				}
				s = e;
			}

			input = output.slice();
			output = [];
			s = input[input.length - 1];
			for (let i = 0, n = input.length; i < n; ++i) {
				const e = input[i];
				if (e.coord[j] <= e.coord[3]) {
					if (s.coord[j] > s.coord[3]) {
						output.push(this._intersect(s, e, j, false));
					}
					output.push(e);
				} else if (s.coord[j] <= s.coord[3]) {
					output.push(this._intersect(s, e, j, false));
				}
				s = e;
			}
		}

		return output;
	}

	protected _predraw(camera: CameraNode, viewPerspective: mat4, transform: mat4) {
		if (!this.resolutions)
			return;

		// The quad is transformed, clipped and projected in software to
		// determine screen coverage for accurate LOD tesselation and
		// texture selection.
		const mvp = this._mvp;
		const vp = viewPerspective;
		const mm = transform ?
				mat4.mul(this._mm, transform, this.modelMatrix) :
				mat4.copy(this._mm, this.modelMatrix);

		mat4.mul(mvp, vp, mm);

		// Calculate the four quad vertices in homogeneous clip space.
		const topLeft = vec4.transformMat4(this._topLeft, this._unitTopLeft, mvp);
		const topRight = vec4.transformMat4(this._topRight, this._unitTopRight, mvp);
		const bottomLeft = vec4.transformMat4(this._bottomLeft, this._unitBottomLeft, mvp);
		const bottomRight = vec4.transformMat4(this._bottomRight, this._unitBottomRight, mvp);

		// Create texture coordinate attributes for each of the quad vertices
		// from the global content coordinates. These will be interpolated during
		// clipping.
		vec2.set(this._topLeftTexCoord, this.contentLeft, this.contentTop);
		vec2.set(this._topRightTexCoord, this.contentLeft + this.contentWidth, this.contentTop);
		vec2.set(this._bottomLeftTexCoord, this.contentLeft, this.contentTop + this.contentHeight);
		vec2.set(this._bottomRightTexCoord, this.contentLeft + this.contentWidth, this.contentTop + this.contentHeight);

		// Clip the quad against the screen and interpolate texture coordinates.
		// The output of this step can generate more vertices than were passed in.
		const clipped = TiledImageNode._clip([
			{ coord: topLeft, texCoord: this._topLeftTexCoord },
			{ coord: topRight, texCoord: this._topRightTexCoord },
			{ coord: bottomRight, texCoord: this._bottomRightTexCoord },
			{ coord: bottomLeft, texCoord: this._bottomLeftTexCoord }
		]);

		// If insufficient vertices survived clipping, we're done.
		if (clipped.length < 3)
			return;

		// Perform perspective division now that clipping is complete.
		// http://research.microsoft.com/pubs/73937/p245-blinn.pdf
		for (let i = 0; i < clipped.length; ++i)
			for (let j = 0; j < 4; ++j)
				clipped[i].coord[j] /= clipped[i].coord[3];

		// Calculate a axis-aligned bounding box in device coordinates
		// that contains the vertices after clipping.
		let coordLeft = clipped[0].coord[0];
		let coordRight = clipped[0].coord[0];
		let coordTop = clipped[0].coord[1];
		let coordBottom = clipped[0].coord[1];
		for (let i = 0; i < clipped.length; ++i) {
			coordLeft = Math.min(coordLeft, clipped[i].coord[0]);
			coordRight = Math.max(coordRight, clipped[i].coord[0]);
			coordTop = Math.min(coordTop, clipped[i].coord[1]);
			coordBottom = Math.max(coordBottom, clipped[i].coord[1]);
		}
		const coordWidth = Math.abs(coordRight - coordLeft);
		const coordHeight = Math.abs(coordBottom - coordTop);

		// Calculate the number of pixels used by the screen-space AABB.
		const horizontalPixels = coordWidth / 2 * camera.viewportWidth;
		const verticalPixels = coordHeight / 2 * camera.viewportHeight;
		if (horizontalPixels < 1 && verticalPixels < 1)
			return;


		// Calculate an axis aligned bounding box in content coordinates
		// that contains the content polygon after clipping.
		let visibleLeft = clipped[0].texCoord[0];
		let visibleRight = clipped[0].texCoord[0];
		let visibleTop = clipped[0].texCoord[1];
		let visibleBottom = clipped[0].texCoord[1];
		for (let i = 0; i < clipped.length; ++i) {
			visibleLeft = Math.min(visibleLeft, clipped[i].texCoord[0]);
			visibleRight = Math.max(visibleRight, clipped[i].texCoord[0]);
			visibleTop = Math.min(visibleTop, clipped[i].texCoord[1]);
			visibleBottom = Math.max(visibleBottom, clipped[i].texCoord[1]);
		}
		const visibleWidth = Math.abs(visibleRight - visibleLeft);
		const visibleHeight = Math.abs(visibleBottom - visibleTop);
		const visibleRect = this._visibleRect;
		visibleRect.left = visibleLeft;
		visibleRect.top = visibleTop;
		visibleRect.width = visibleWidth;
		visibleRect.height = visibleHeight;

		// Calculate the ratio of content pixels to screen pixels using Manhatten
		// distance because this is what GPUs typically use for MIP selection.
		//
		// However, since we're using the corners of the quad for
		// the ratio measurement, on an angle the entire quad can be visible which
		// (for a quad representing an entire flyer) causes a very large scale and
		// the lowest detail MIP to be used.
		const batch = this.scene.tileBatch;
		const scale = (visibleWidth + visibleHeight) /
				(horizontalPixels + verticalPixels);

		// Select a tile resolution based on the effective LOD bias and the
		// content to screen pixel ratio.

		const resolution =
				preferredResolution(256, 256, scale, this.resolutions) -
				batch.atlas.lodBias;

		// Load tiles and add them to the vertex buffer for drawing.
		forEachTile(this.resolutions, 0,
								this.contentLeft,
								this.contentTop,
								this.contentWidth,
								this.contentHeight, visibleRect, this._overlap,
								this._tileSize,
								(z: number, x: number, y: number, maxY: number,
									left: number, top: number, width: number,
									height: number, srcLeft: number, srcTop: number,
									srcWidth: number, srcHeight: number) => {
			const slot = this._tileImageLoader.get(this._path, z, x, y, maxY,
					false, this.resolutions.length, this._tileSize, this._suffix);
			if (slot.img)
				batch.atlas.touch(slot.img);
		});

		// Load tiles and add them to the vertex buffer for drawing.
		forEachTile(this.resolutions, resolution,
								this.contentLeft,
								this.contentTop,
								this.contentWidth,
								this.contentHeight, visibleRect, this._overlap,
								this._tileSize,
								(z: number, x: number, y: number, maxY: number,
									left: number, top: number, width: number,
									height: number, srcLeft: number, srcTop: number,
									srcWidth: number, srcHeight: number) => {
			const slot = this._tileImageLoader.get(this._path, z, x, y, maxY,
					false, this.resolutions.length, this._tileSize, this._suffix);
			if (slot.img)
				batch.atlas.touch(slot.img);
		});
	}

	protected _draw(camera: CameraNode, viewPerspective: mat4, transform: mat4) {
		if (!this.resolutions)
			return;

		// The quad is transformed, clipped and projected in software to
		// determine screen coverage for accurate LOD tesselation and
		// texture selection.
		const mvp = this._mvp;
		const vp = viewPerspective;
		const mm = transform ?
				mat4.mul(this._mm, transform, this.modelMatrix) :
				mat4.copy(this._mm, this.modelMatrix);

		mat4.mul(mvp, vp, mm);

		// Calculate the four quad vertices in homogeneous clip space.
		const topLeft = vec4.transformMat4(this._topLeft, this._unitTopLeft, mvp);
		const topRight = vec4.transformMat4(this._topRight, this._unitTopRight, mvp);
		const bottomLeft = vec4.transformMat4(this._bottomLeft, this._unitBottomLeft, mvp);
		const bottomRight = vec4.transformMat4(this._bottomRight, this._unitBottomRight, mvp);

		// Create texture coordinate attributes for each of the quad vertices
		// from the global content coordinates. These will be interpolated during
		// clipping.
		vec2.set(this._topLeftTexCoord, this.contentLeft, this.contentTop);
		vec2.set(this._topRightTexCoord, this.contentLeft + this.contentWidth, this.contentTop);
		vec2.set(this._bottomLeftTexCoord, this.contentLeft, this.contentTop + this.contentHeight);
		vec2.set(this._bottomRightTexCoord, this.contentLeft + this.contentWidth, this.contentTop + this.contentHeight);

		// Clip the quad against the screen and interpolate texture coordinates.
		// The output of this step can generate more vertices than were passed in.
		const clipped = TiledImageNode._clip([
			{ coord: topLeft, texCoord: this._topLeftTexCoord },
			{ coord: topRight, texCoord: this._topRightTexCoord },
			{ coord: bottomRight, texCoord: this._bottomRightTexCoord },
			{ coord: bottomLeft, texCoord: this._bottomLeftTexCoord }
		]);

		// If insufficient vertices survived clipping, we're done.
		if (clipped.length < 3)
			return;

		// Perform perspective division now that clipping is complete.
		// http://research.microsoft.com/pubs/73937/p245-blinn.pdf
		for (let i = 0; i < clipped.length; ++i)
			for (let j = 0; j < 4; ++j)
				clipped[i].coord[j] /= clipped[i].coord[3];

		// Calculate a axis-aligned bounding box in device coordinates
		// that contains the vertices after clipping.
		let coordLeft = clipped[0].coord[0];
		let coordRight = clipped[0].coord[0];
		let coordTop = clipped[0].coord[1];
		let coordBottom = clipped[0].coord[1];
		for (let i = 0; i < clipped.length; ++i) {
			coordLeft = Math.min(coordLeft, clipped[i].coord[0]);
			coordRight = Math.max(coordRight, clipped[i].coord[0]);
			coordTop = Math.min(coordTop, clipped[i].coord[1]);
			coordBottom = Math.max(coordBottom, clipped[i].coord[1]);
		}
		const coordWidth = Math.abs(coordRight - coordLeft);
		const coordHeight = Math.abs(coordBottom - coordTop);

		// Calculate the number of pixels used by the screen-space AABB.
		const horizontalPixels = coordWidth / 2 * camera.viewportWidth;
		const verticalPixels = coordHeight / 2 * camera.viewportHeight;
		if (horizontalPixels < 1 && verticalPixels < 1)
			return;


		// Calculate an axis aligned bounding box in content coordinates
		// that contains the content polygon after clipping.
		let visibleLeft = clipped[0].texCoord[0];
		let visibleRight = clipped[0].texCoord[0];
		let visibleTop = clipped[0].texCoord[1];
		let visibleBottom = clipped[0].texCoord[1];
		for (let i = 0; i < clipped.length; ++i) {
			visibleLeft = Math.min(visibleLeft, clipped[i].texCoord[0]);
			visibleRight = Math.max(visibleRight, clipped[i].texCoord[0]);
			visibleTop = Math.min(visibleTop, clipped[i].texCoord[1]);
			visibleBottom = Math.max(visibleBottom, clipped[i].texCoord[1]);
		}
		const visibleWidth = Math.abs(visibleRight - visibleLeft);
		const visibleHeight = Math.abs(visibleBottom - visibleTop);
		const visibleRect = this._visibleRect;
		visibleRect.left = visibleLeft;
		visibleRect.top = visibleTop;
		visibleRect.width = visibleWidth;
		visibleRect.height = visibleHeight;

		// Calculate the ratio of content pixels to screen pixels using Manhatten
		// distance because this is what GPUs typically use for MIP selection.
		//
		// However, since we're using the corners of the quad for
		// the ratio measurement, on an angle the entire quad can be visible which
		// (for a quad representing an entire flyer) causes a very large scale and
		// the lowest detail MIP to be used.
		const batch = this.scene.tileBatch;
		this.scale = (visibleWidth + visibleHeight) /
				(horizontalPixels + verticalPixels);

		// Select a tile resolution based on the effective LOD bias and the
		// content to screen pixel ratio.
		const resolution =
				preferredResolution(256, 256, this.scale, this.resolutions) -
				batch.atlas.lodBias;

		let loadCount = 0;
		const safeResolution = 0;

		const loadRect = this._loadRect;
		loadRect.left = visibleLeft;
		loadRect.top = visibleTop;
		loadRect.width = visibleWidth * 2;
		loadRect.height = visibleHeight;

		// Perform a pass of tile loading at a higher MIP level so that there is
		// conservatively always something to show.
		forEachTile(this.resolutions, safeResolution,
								this.contentLeft,
								this.contentTop,
								this.contentWidth,
								this.contentHeight, loadRect, this._overlap,
								this._tileSize,
								(z: number, x: number, y: number, maxY: number,
									left: number, top: number, width: number,
									height: number, srcLeft: number, srcTop: number,
									srcWidth: number, srcHeight: number) => {
			const slot = this._tileImageLoader.get(this._path, z, x, y, maxY,
					false, this.resolutions.length, this._tileSize, this._suffix);
			if (!slot.img && loadCount < 3) {
				this._tileImageLoader.load(this._path, z, x, y, maxY, this._suffix);
				++loadCount;
			}
		});

		forEachTile(this.resolutions, safeResolution,
								this.contentLeft,
								this.contentTop,
								this.contentWidth,
								this.contentHeight, visibleRect, this._overlap,
								this._tileSize,
								(z: number, x: number, y: number, maxY: number,
									left: number, top: number, width: number,
									height: number, srcLeft: number, srcTop: number,
									srcWidth: number, srcHeight: number) => {
			const slot = this._tileImageLoader.get(this._path, z, x, y, maxY, false,
					this.resolutions.length, this._tileSize, this._suffix);
			if (!slot.img)
				return;

			// Adjust tile coordinates by scale and bias when using higher MIPs
			// as fallbacks for the current MIP which is not available yet.
			const img = slot.img;
			srcLeft = (srcLeft - this._overlap) * slot.scale + this._overlap;
			srcTop = (srcTop - this._overlap) * slot.scale + this._overlap;
			srcWidth *= slot.scale;
			srcHeight *= slot.scale;
			srcLeft += slot.bias_x;
			srcTop += slot.bias_y;

			this._drawTile({ uploadCount: -1 }, img, safeResolution, left, top,
										width, height,
										srcLeft, srcTop, srcWidth, srcHeight, visibleRect,
										slot.scale, batch);
		});

		const upload = {
			uploadCount: 0
		};

		// Load tiles and add them to the vertex buffer for drawing.
		forEachTile(this.resolutions, resolution,
								this.contentLeft,
								this.contentTop,
								this.contentWidth,
								this.contentHeight, visibleRect, this._overlap,
								this._tileSize,
								(z: number, x: number, y: number, maxY: number,
									left: number, top: number, width: number,
									height: number, srcLeft: number, srcTop: number,
									srcWidth: number, srcHeight: number) => {
			let slot = this._tileImageLoader.get(this._path, z, x, y, maxY,
					false, this.resolutions.length, this._tileSize, this._suffix);
			if (!slot.img && loadCount < 3) {
				this._tileImageLoader.load(this._path, z, x, y, maxY, this._suffix);
				++loadCount;
			}

			slot = this._tileImageLoader.get(this._path, z, x, y, maxY, false,
					this.resolutions.length, this._tileSize, this._suffix);
			if (!slot.img)
				return;

			// Adjust tile coordinates by scale and bias when using higher MIPs
			// as fallbacks for the current MIP which is not available yet.
			const img = slot.img;
			srcLeft = (srcLeft - this._overlap) * slot.scale + this._overlap;
			srcTop = (srcTop - this._overlap) * slot.scale + this._overlap;
			srcWidth *= slot.scale;
			srcHeight *= slot.scale;
			srcLeft += slot.bias_x;
			srcTop += slot.bias_y;

			this._drawTile(upload, img, resolution, left, top, width, height,
										srcLeft, srcTop, srcWidth, srcHeight, visibleRect,
										slot.scale, batch);
		});
	}

	private _drawTile(
		upload: { uploadCount: number },
		img: HTMLImageElement, resolution: number, left: number,
		top: number, width: number, height: number,
		srcLeft: number, srcTop: number, srcWidth: number,
		srcHeight: number,
		rect: Rect,
		bias_scale: number, batch: TileBatch) {
		this._topLeftContent[0] = left / this.contentWidth;
		this._topLeftContent[1] = top / this.contentHeight;
		this._topRightContent[0] = (left + width) / this.contentWidth;
		this._topRightContent[1] = top / this.contentHeight;
		this._bottomLeftContent[0] = left / this.contentWidth;
		this._bottomLeftContent[1] = (top + height) / this.contentHeight;
		this._bottomRightContent[0] = (left + width) / this.contentWidth;
		this._bottomRightContent[1] = (top + height) / this.contentHeight;

		const mm = this._mm;
		const topLeft = vec4.transformMat4(
				this._topLeft, this._topLeftContent, mm);
		const topRight = vec4.transformMat4(
				this._topRight, this._topRightContent, mm);
		const bottomLeft = vec4.transformMat4(
				this._bottomLeft, this._bottomLeftContent, mm);
		const bottomRight = vec4.transformMat4(
				this._bottomRight, this._bottomRightContent, mm);

		const rc = batch.atlas.getTile(upload, img, resolution, left, top, width,
																	height, rect, this.resolutions);
		if (!rc)
			return false;

		const slot = <TileAtlasSlot>rc;

		const pixelOffset = 0.5 * bias_scale;
		const u0 = slot.u0 + (pixelOffset + srcLeft) / slot.maxSize;
		const tileImageSize = this._tileSize + this._overlap * 2;
		const v0 =
				slot.v0 + (pixelOffset + tileImageSize - srcTop - srcHeight) /
						slot.maxSize;
		const u1 = u0 + (srcWidth - 2 * pixelOffset) / slot.maxSize;
		const v1 = v0 + (srcHeight - 2 * pixelOffset) / slot.maxSize;

		let data = batch.buffer;
		let i = batch.bufferFill;
		if (i + 30 >= data.length)
			data = batch.grow();

		// 6 vertices, vertex position and texture coordinates interleaved.
		data[i++] = bottomLeft[0]; data[i++] = bottomLeft[1]; data[i++] = bottomLeft[2];
		data[i++] = u0;
		data[i++] = v0;

		data[i++] = topRight[0]; data[i++] = topRight[1]; data[i++] = topRight[2];
		data[i++] = u1;
		data[i++] = v1;

		data[i++] = topLeft[0]; data[i++] = topLeft[1]; data[i++] = topLeft[2];
		data[i++] = u0;
		data[i++] = v1;

		data[i++] = topRight[0]; data[i++] = topRight[1]; data[i++] = topRight[2];
		data[i++] = u1;
		data[i++] = v1;

		data[i++] = bottomLeft[0]; data[i++] = bottomLeft[1]; data[i++] = bottomLeft[2];
		data[i++] = u0;
		data[i++] = v0;

		data[i++] = bottomRight[0]; data[i++] = bottomRight[1]; data[i++] = bottomRight[2];
		data[i++] = u1;
		data[i++] = v0;

		batch.bufferFill = i;

		if (this.scene.debugRendering) {
			const linesBuffer = this.scene.linesBuffer;
			linesBuffer.push(topLeft[0]);
			linesBuffer.push(topLeft[1]);
			linesBuffer.push(topLeft[2]);
			linesBuffer.push(bottomLeft[0]);
			linesBuffer.push(bottomLeft[1]);
			linesBuffer.push(bottomLeft[2]);
			linesBuffer.push(topLeft[0]);
			linesBuffer.push(topLeft[1]);
			linesBuffer.push(topLeft[2]);
			linesBuffer.push(topRight[0]);
			linesBuffer.push(topRight[1]);
			linesBuffer.push(topRight[2]);
			linesBuffer.push(bottomLeft[0]);
			linesBuffer.push(bottomLeft[1]);
			linesBuffer.push(bottomLeft[2]);
			linesBuffer.push(topRight[0]);
			linesBuffer.push(topRight[1]);
			linesBuffer.push(topRight[2]);
			linesBuffer.push(topRight[0]);
			linesBuffer.push(topRight[1]);
			linesBuffer.push(topRight[2]);
			linesBuffer.push(bottomRight[0]);
			linesBuffer.push(bottomRight[1]);
			linesBuffer.push(bottomRight[2]);
			linesBuffer.push(bottomLeft[0]);
			linesBuffer.push(bottomLeft[1]);
			linesBuffer.push(bottomLeft[2]);
			linesBuffer.push(bottomRight[0]);
			linesBuffer.push(bottomRight[1]);
			linesBuffer.push(bottomRight[2]);
		}

		return true;
	}
}

interface ItemClipping {
	id: string;
	rect: Rect;
	modelViewMatrix: mat4;
}

interface ItemHover {
	id: number;
	rect: Rect;
	opacity: number;
	modelViewMatrix: mat4;
}

interface PageItem {
	rect: Rect;
	name: string;
	id: number;
}

class FlyerPageNode extends TiledImageNode {
	items: PageItem[];
	coupons: FlyerItemCoupon[];
	clippedCoupons: number[];

	protected _focus: Rect;
	protected _hovers: ItemHover[] = [];
	protected _lastHover: number;
	protected _clippings: { [id: string]: ItemClipping } = {};

	private _highlightTopLeft = vec4.fromValues(0, 0, 0, 1);
	private _highlightTopRight = vec4.fromValues(0, 0, 0, 1);
	private _highlightBottomLeft = vec4.fromValues(0, 0, 0, 1);
	private _highlightTottomRight = vec4.fromValues(0, 0, 0, 1);
	private _highlightTopLeftContent = vec4.fromValues(0, 0, 0, 1);
	private _highlightTopRightContent = vec4.fromValues(0, 0, 0, 1);
	private _highlightBottomLeftContent = vec4.fromValues(0, 0, 0, 1);
	private _highlightTottomRightContent = vec4.fromValues(0, 0, 0, 1);

	getFocus() {
		return this._focus;
	}

	setFocus(rect: Rect) {
		this._focus = rect ? rect.clone() : null;
		this.scene.invalidate();
	}

	addClipping(clipping: Clipping) {
		if (clipping.type === ClippingType.ITEM) {
			const item = clipping.item as SLItemClippingDetails;
			const details = item.details;
			const rect = new Rect(details.left, -details.top,
														details.right - details.left,
														details.top - details.bottom);
			rect.scaleAbout(1.2, {
				x: rect.left + rect.width / 2,
				y: rect.top + rect.height / 2
			});

			this._clippings[clipping.item.id] = {
				id: clipping.item.id,
				rect: rect,
				modelViewMatrix: mat4.create(),
			};
		}
	}

	clearClippings() {
		this._clippings = {};
	}

	setClippedCoupons(coupons: number[]) {
		this.clippedCoupons = coupons;
		this.scene.invalidate();
	}

	addHover(item: PageItem) {
		if (!item) {
			this._lastHover = undefined;
			return;
		}

		const id = item.id;
		const value = item.rect;
		if (this._hovers[id] || id === this._lastHover)
			return;

		this._lastHover = id;
		this._hovers[id] = {
			id: id,
			rect: value,
			opacity: 0,
			modelViewMatrix: mat4.create(),
		};

		const a = new Animation(100, "ease-out", (frac: number) => {
			this._hovers[id].opacity = frac / 2;
			this.scene.invalidate();
		});
		a.onfinish = () => {
			const a = new Animation(400, "ease-out", (frac: number) => {
				this._hovers[id].opacity = 0.5 - frac / 2;
				this.scene.invalidate();
			});
			a.onfinish = () => {
				delete this._hovers[id];
			};
			a.play();
			this.scene.invalidate();
		};
		a.play();
		this.scene.invalidate();
	}

	protected _draw(camera: CameraNode, viewPerspective: mat4, transform: mat4) {
		super._draw(camera, viewPerspective, transform);

		for (const k in this._clippings) {
			if (!this._clippings.hasOwnProperty(k))
				continue;

			const clipping = this._clippings[k];

			this._drawBadge(this.scene.badgeBuffer,
											clipping.rect.left,
											clipping.rect.top,
											clipping.rect.width,
											clipping.rect.height,
											this._mm,
											0, 0.5, 0.5, 1);
		}

		const badgeSize = Math.min(COUPON_BADGE_MAX,
				COUPON_BADGE_SIZE * this.scale);
		for (const k in this.coupons) {
			if (!this.coupons.hasOwnProperty(k))
				continue;

			const coupon = this.coupons[k];

			/* Change the badge color based on if ANY coupon for this item has been clipped.
				* There are cases on hte P&G Brandsaver where an 'item' can have multiple coupons
				* and we need to check if ANY of those have been clipped to change the colour. */
			const matchups = this.coupons
				.filter((entry) => entry.item.id === coupon.item.id)
				.filter((entry) => this.clippedCoupons.indexOf(entry.couponId) > -1);

			/* If coupon is clipped, show the yellow badge */
			let badgeCoords = [0.5, 0, 1, 0.5];
			if (matchups.length > 0) {
				badgeCoords = [0, 0, 0.5, 0.5];
			}

			this._drawBadge(this.scene.badgeBuffer,
											coupon.right - badgeSize * 0.7,
											-coupon.top - badgeSize * 0.4,
											badgeSize,
											badgeSize,
											this._mm,
											badgeCoords[0], badgeCoords[1],
											badgeCoords[2], badgeCoords[3]);
		}

		for (const k in this._hovers) {
			if (!this._hovers.hasOwnProperty(k))
				continue;

			const hover = this._hovers[k];
			this._drawHoverBuffer(this.scene.hoverBuffer,
														hover.rect.left,
														hover.rect.top,
														hover.rect.width,
														hover.rect.height,
														this._mm, hover.opacity);
		}

		if (this._focus &&
				!document.documentElement.hasAttribute("hide-focus-outline"))
			this._drawFocusBuffer(this.scene.focusBuffer,
														this._focus.left,
														this._focus.top,
														this._focus.width,
														this._focus.height,
														this._mm);
	}

	private _drawFocusBuffer(data: Float32Vector, left: number, top: number, width: number, height: number, mm: mat4) {
		this._highlightTopLeftContent[0] = (left - this.contentLeft) / this.contentWidth;
		this._highlightTopLeftContent[1] = (top - this.contentTop) / this.contentHeight;
		this._highlightTopRightContent[0] = (left - this.contentLeft + width) / this.contentWidth;
		this._highlightTopRightContent[1] = (top - this.contentTop) / this.contentHeight;
		this._highlightBottomLeftContent[0] = (left - this.contentLeft) / this.contentWidth;
		this._highlightBottomLeftContent[1] = (top - this.contentTop + height) / this.contentHeight;
		this._highlightTottomRightContent[0] = (left - this.contentLeft + width) / this.contentWidth;
		this._highlightTottomRightContent[1] = (top - this.contentTop + height) / this.contentHeight;

		const topLeft = vec4.transformMat4(
				this._highlightTopLeft, this._highlightTopLeftContent, mm);
		const topRight = vec4.transformMat4(
				this._highlightTopRight, this._highlightTopRightContent, mm);
		const bottomLeft = vec4.transformMat4(
				this._highlightBottomLeft, this._highlightBottomLeftContent, mm);
		const bottomRight = vec4.transformMat4(
				this._highlightTottomRight, this._highlightTottomRightContent, mm);

		data.push(bottomLeft[0]);
		data.push(bottomLeft[1]);
		data.push(bottomLeft[2]);
		data.push(0);
		data.push(0);

		data.push(topRight[0]);
		data.push(topRight[1]);
		data.push(topRight[2]);
		data.push(1);
		data.push(1);

		data.push(topLeft[0]);
		data.push(topLeft[1]);
		data.push(topLeft[2]);
		data.push(0);
		data.push(1);

		data.push(topRight[0]);
		data.push(topRight[1]);
		data.push(topRight[2]);
		data.push(1);
		data.push(1);

		data.push(bottomLeft[0]);
		data.push(bottomLeft[1]);
		data.push(bottomLeft[2]);
		data.push(0);
		data.push(0);

		data.push(bottomRight[0]);
		data.push(bottomRight[1]);
		data.push(bottomRight[2]);
		data.push(1);
		data.push(0);
	}

	private _drawBadge(
		data: Float32Vector, left: number, top: number,
		width: number, height: number, mm: mat4,
		u0: number, v0: number, u1: number, v1: number) {
		this._highlightTopLeftContent[0] = (left - this.contentLeft) / this.contentWidth;
		this._highlightTopLeftContent[1] = (top - this.contentTop) / this.contentHeight;
		this._highlightTopRightContent[0] = (left - this.contentLeft + width) / this.contentWidth;
		this._highlightTopRightContent[1] = (top - this.contentTop) / this.contentHeight;
		this._highlightBottomLeftContent[0] = (left - this.contentLeft) / this.contentWidth;
		this._highlightBottomLeftContent[1] = (top - this.contentTop + height) / this.contentHeight;
		this._highlightTottomRightContent[0] = (left - this.contentLeft + width) / this.contentWidth;
		this._highlightTottomRightContent[1] = (top - this.contentTop + height) / this.contentHeight;

		const topLeft = vec4.transformMat4(
				this._highlightTopLeft, this._highlightTopLeftContent, mm);
		const topRight = vec4.transformMat4(
				this._highlightTopRight, this._highlightTopRightContent, mm);
		const bottomLeft = vec4.transformMat4(
				this._highlightBottomLeft, this._highlightBottomLeftContent, mm);
		const bottomRight = vec4.transformMat4(
				this._highlightTottomRight, this._highlightTottomRightContent, mm);

		data.push(bottomLeft[0]);
		data.push(bottomLeft[1]);
		data.push(bottomLeft[2]);
		data.push(u0);
		data.push(v0);

		data.push(topRight[0]);
		data.push(topRight[1]);
		data.push(topRight[2]);
		data.push(u1);
		data.push(v1);

		data.push(topLeft[0]);
		data.push(topLeft[1]);
		data.push(topLeft[2]);
		data.push(u0);
		data.push(v1);

		data.push(topRight[0]);
		data.push(topRight[1]);
		data.push(topRight[2]);
		data.push(u1);
		data.push(v1);

		data.push(bottomLeft[0]);
		data.push(bottomLeft[1]);
		data.push(bottomLeft[2]);
		data.push(u0);
		data.push(v0);

		data.push(bottomRight[0]);
		data.push(bottomRight[1]);
		data.push(bottomRight[2]);
		data.push(u1);
		data.push(v0);
	}

	private _drawHoverBuffer(
		data: Float32Vector, left: number, top: number,
		width: number, height: number, mm: mat4,
		opacity: number) {
		this._highlightTopLeftContent[0] = (left - this.contentLeft) / this.contentWidth;
		this._highlightTopLeftContent[1] = (top - this.contentTop) / this.contentHeight;
		this._highlightTopRightContent[0] = (left - this.contentLeft + width) / this.contentWidth;
		this._highlightTopRightContent[1] = (top - this.contentTop) / this.contentHeight;
		this._highlightBottomLeftContent[0] = (left - this.contentLeft) / this.contentWidth;
		this._highlightBottomLeftContent[1] = (top - this.contentTop + height) / this.contentHeight;
		this._highlightTottomRightContent[0] = (left - this.contentLeft + width) / this.contentWidth;
		this._highlightTottomRightContent[1] = (top - this.contentTop + height) / this.contentHeight;

		const topLeft = vec4.transformMat4(
				this._highlightTopLeft, this._highlightTopLeftContent, mm);
		const topRight = vec4.transformMat4(
				this._highlightTopRight, this._highlightTopRightContent, mm);
		const bottomLeft = vec4.transformMat4(
				this._highlightBottomLeft, this._highlightBottomLeftContent, mm);
		const bottomRight = vec4.transformMat4(
				this._highlightTottomRight, this._highlightTottomRightContent, mm);

		const r = 1;
		const g = 1;
		const b = 1;
		const a = opacity;

		data.push(bottomLeft[0]);
		data.push(bottomLeft[1]);
		data.push(bottomLeft[2]);
		data.push(r);
		data.push(g);
		data.push(b);
		data.push(a);

		data.push(topRight[0]);
		data.push(topRight[1]);
		data.push(topRight[2]);
		data.push(r);
		data.push(g);
		data.push(b);
		data.push(a);

		data.push(topLeft[0]);
		data.push(topLeft[1]);
		data.push(topLeft[2]);
		data.push(r);
		data.push(g);
		data.push(b);
		data.push(a);

		data.push(topRight[0]);
		data.push(topRight[1]);
		data.push(topRight[2]);
		data.push(r);
		data.push(g);
		data.push(b);
		data.push(a);

		data.push(bottomLeft[0]);
		data.push(bottomLeft[1]);
		data.push(bottomLeft[2]);
		data.push(r);
		data.push(g);
		data.push(b);
		data.push(a);

		data.push(bottomRight[0]);
		data.push(bottomRight[1]);
		data.push(bottomRight[2]);
		data.push(r);
		data.push(g);
		data.push(b);
		data.push(a);
	}
}

class Scene {

	@inject config: ConfigService;

	camera: CameraNode;
	debugCamera: CameraNode;
	viewCamera: CameraNode;
	_nodes: SceneNode[] = [];

	bumpBias: boolean = false;
	undersubscribedCount:  number = 0;

	now: number = 0;

	protected _invalidated: boolean = false;

	tileBatch: TileBatch;
	textureProgram: TextureProgram;
	unitQuadVertexBuffer: TexturedQuadVertexBuffer;
	fillProgram: ColorProgram;
	coloredQuadProgram: AttributeColorProgram;
	coloredQuadBorderProgram: BorderProgram;
	vertexBuffer: WebGLBuffer;
	hoverBuffer = new Float32Vector;
	hoverVertexBuffer: WebGLBuffer;

	badgeBuffer = new Float32Vector;
	badgeVertexBuffer: WebGLBuffer;

	focusBuffer = new Float32Vector;
	focusVertexBuffer: WebGLBuffer;

	linesBuffer = new Float32Vector;
	linesVertexBuffer: WebGLBuffer;

	oninvalidate: () => void;

	private _debugRendering = false;
	private _frame: number = 0;
	private _overlayFramebuffer: WebGLFramebuffer;
	private _overlayTexture: WebGLTexture;
	private _overlayRenderbuffer: WebGLRenderbuffer;
	private _badgesTexture: WebGLTexture;
	private _vp = mat4.create();

	constructor(public _gl: WebGLRenderingContext) {
		timeline.onplay = this._handlePlay;
	}

	get invalidated() {
		return this._invalidated;
	}

	get debugRendering() {
		return this._debugRendering;
	}

	set debugRendering(value: boolean) {
		this._debugRendering = value;
		this.invalidate();
	}

	get nodes() {
		return this._nodes;
	}

	set nodes(value: SceneNode[]) {
		this._nodes = value;
		if (this._gl) {
			for (let i = 0, n = (this._nodes || []).length; i < n; ++i) {
				const node = this._nodes[i];
				node.handleContextRestored(this._gl);
			}
		}
	}

	handleContextRestored() {
		const gl = this._gl;
		gl.getExtension("WEBGL_lose_context");

		this._overlayFramebuffer = gl.createFramebuffer();
		gl.bindFramebuffer(gl.FRAMEBUFFER, this._overlayFramebuffer);

		this._overlayTexture = gl.createTexture();
		gl.bindTexture(gl.TEXTURE_2D, this._overlayTexture);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 512, 512, 0, gl.RGBA,
									gl.UNSIGNED_BYTE, null);

		this._overlayRenderbuffer = gl.createRenderbuffer();
		gl.bindRenderbuffer(gl.RENDERBUFFER, this._overlayRenderbuffer);
		gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, 512, 512);

		gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0,
														gl.TEXTURE_2D, this._overlayTexture, 0);
		gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT,
															gl.RENDERBUFFER, this._overlayRenderbuffer);

		gl.bindRenderbuffer(gl.RENDERBUFFER, null);
		gl.bindFramebuffer(gl.FRAMEBUFFER, null);
		gl.bindTexture(gl.TEXTURE_2D, null);

		gl.disable(gl.DEPTH_TEST);
		gl.depthFunc(gl.LEQUAL);

		gl.disable(gl.BLEND);
		gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

		this.tileBatch = new TileBatch(gl, 256);
		this.textureProgram = new TextureProgram(gl);
		this.unitQuadVertexBuffer = new TexturedQuadVertexBuffer(gl);
		this.fillProgram = new ColorProgram(this._gl);
		this.coloredQuadProgram = new AttributeColorProgram(this._gl);
		this.coloredQuadBorderProgram = new BorderProgram(this._gl);
		this.unitQuadVertexBuffer.bind();
		this.badgeVertexBuffer = gl.createBuffer();
		this.hoverVertexBuffer = gl.createBuffer();
		this.focusVertexBuffer = gl.createBuffer();
		this.linesVertexBuffer = gl.createBuffer();

		const viewport = gl.getParameter(gl.VIEWPORT);

		this.camera = new CameraNode;
		this.camera.viewportWidth = viewport[2];
		this.camera.viewportHeight = viewport[3];
		this.camera.scene = this;

		this.debugCamera = new CameraNode;
		this.debugCamera.viewportWidth = viewport[2];
		this.debugCamera.viewportHeight = viewport[3];
		this.debugCamera.scene = this;

		this.viewCamera = this.camera;

		gl.clearColor(0.894, 0.894, 0.894, 1);
		gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
		gl.viewport(0, 0, this.viewCamera.viewportWidth,
								this.viewCamera.viewportHeight);

		const badgesImage = new Image;
		badgesImage.onload = () => {
			this._badgesTexture = gl.createTexture();
			gl.bindTexture(gl.TEXTURE_2D, this._badgesTexture);
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA,
										gl.UNSIGNED_BYTE, badgesImage);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
			gl.generateMipmap(gl.TEXTURE_2D);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
			gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
			gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, <any>true);
			gl.bindTexture(gl.TEXTURE_2D, null);
		};
		const runtimeExt = this.config.RUNTIME === RuntimeConfig.PG ? "pg" : "default";
		badgesImage.crossOrigin = "Anonymous";
		badgesImage.src = `https://f.wishabi.net/creative/Distribution/images/badges-${runtimeExt}.png`;

		for (let i = 0, n = (this._nodes || []).length; i < n; ++i) {
			const node = this._nodes[i];
			node.handleContextRestored(this._gl);
		}
	}

	refreshViewport() {
		this._gl.viewport(0, 0, this.viewCamera.viewportWidth,
											this.viewCamera.viewportHeight);
	}

	invalidate(callback = true) {
		if (!this._invalidated) {
			this._invalidated = true;
			// this._animationFrame = requestAnimationFrame(this._draw);
			if (this.oninvalidate && callback)
				this.oninvalidate();
		}
	}

	draw() {
		if (!this._invalidated)
			return;

		const batch = this.tileBatch;
		if (!batch)
			return;

		timeline.sample(flipp.now());
		this._invalidated = false;

		this.bumpBias = false;

		batch.atlas.onoversubscribed = () => {
			this.bumpBias = true;
		};

		let bumpedBias = false;
		do {
			batch.atlas.frame = ++this._frame;
			this.hoverBuffer.clear();
			this.badgeBuffer.clear();
			this.focusBuffer.clear();
			this.linesBuffer.clear();
			batch.reset();

			this.bumpBias = false;
			this._drawScene(this.viewCamera, null);

			if (this.bumpBias) {
				++batch.atlas.lodBias;
				bumpedBias = true;
			}
		} while (this.bumpBias && batch.atlas.lodBias < 10);

		if (!bumpedBias) {
			++this.undersubscribedCount;
			if (this.undersubscribedCount > 120 && batch.atlas.occupancy < 0.20) {
				this.undersubscribedCount = 0;
				batch.atlas.lodBias = Math.max(0, batch.atlas.lodBias - 1);
			}
		}
	}

	hitTest(screenX: number, screenY: number, callback: (item: PageItem, node: FlyerPageNode) => void) {
		const near = 1;
		const camera = this.viewCamera;
		if (!camera)
			return;

		const projection = camera.projectionMatrix;
		const invProjection = mat4.invert(mat4.create(), projection);

		const cameraMatrix = camera.modelMatrix;
		const invCameraMatrix = camera.invModelMatrix;
		const x = 2 * screenX / camera.viewportWidth - 1;
		const y = 1 - 2 * screenY / camera.viewportHeight;
		const pt = vec4.fromValues(x, y, -1, 1);

		const pt2 = vec4.transformMat4(vec4.create(), pt, invProjection);

		const pt3 = vec4.transformMat4(vec4.create(), pt2, cameraMatrix);

		const rayOrigin = vec3.fromValues(cameraMatrix[12], cameraMatrix[13],
																			cameraMatrix[14]);
		const rayVector = vec3.normalize(vec3.create(),
				vec3.sub(vec3.create(), vec3.fromValues(pt3[0], pt3[1], pt3[2]),
								rayOrigin));

		let bestDepth = -Infinity;
		let finalLeft: number;
		let finalTop: number;
		let finalNode: FlyerPageNode;

		for (let i = 0, n = this.nodes.length; i < n; ++i) {
			const page = <FlyerPageNode>this.nodes[i];
			if (!(page instanceof FlyerPageNode))
				continue;

			const topLeftContent = vec3.fromValues(0, 0, 0);
			const topRightContent = vec3.fromValues(1, 0, 0);
			const bottomLeftContent = vec3.fromValues(0, 1, 0);

			const mm = page.modelMatrix;
			const topLeft = vec3.transformMat4(
				vec3.create(), topLeftContent, mm);
			const topRight = vec3.transformMat4(
				vec3.create(), topRightContent, mm);
			const bottomLeft = vec3.transformMat4(
				vec3.create(), bottomLeftContent, mm);

			const v1 = vec3.sub(vec3.create(), topRight, topLeft);
			const v1Length = vec3.len(v1);
			const v1Norm = vec3.normalize(vec3.create(), v1);
			const v2 = vec3.sub(vec3.create(), bottomLeft, topLeft);
			const v2Length = vec3.len(v2);
			const v2Norm = vec3.normalize(vec3.create(), v2);
			const N = vec3.normalize(vec3.create(), vec3.cross(vec3.create(), v1, v2));
			const d = topLeft[0] * N[0] + topLeft[1] * N[1] + topLeft[2] * N[2];
			const t = -(vec3.dot(rayOrigin, N) - d) / vec3.dot(rayVector, N);
			const P = vec3.add(vec3.create(), rayOrigin,
												vec3.scale(vec3.create(), rayVector, t));

			const RP = vec3.sub(vec3.create(), P, topLeft);

			if (vec3.dot(RP, v1Norm) < 0 || vec3.dot(RP, v2Norm) < 0)
				continue;

			const depth = vec3.transformMat4(
				vec3.create(), P, invCameraMatrix)[2];
			if (depth > -near || depth < bestDepth)
				continue;

			const P0 =
					vec3.scale(vec3.create(), v1Norm, vec3.dot(RP, v1Norm));
			const P0Length = vec3.len(P0);
			const P1 =
					vec3.scale(vec3.create(), v2Norm, vec3.dot(RP, v2Norm));
			const P1Length = vec3.len(P1);

			const pct0 = P0Length / v1Length;
			const pct1 = P1Length / v2Length;
			if (pct0 > 1 || pct1 > 1)
				continue;

			finalLeft = page.contentLeft + pct0 * page.contentWidth;
			finalTop = page.contentTop + pct1 * page.contentHeight;
			finalNode = page;
			bestDepth = depth;
		}
		if (finalNode) {
			let found = false;
			for (let i = 0, n = finalNode.items.length; i < n; ++i) {
				const item = finalNode.items[i];
				if (item.rect.containsPoint(finalLeft, finalTop)) {
					found = true;
					callback(item, finalNode);
					break;
				}
			}
			if (!found)
				callback(null, finalNode);
		}
	}

	private _handlePlay = () => {
		this.invalidate();
	}

	private _drawScene(camera: CameraNode, target: WebGLFramebuffer) {
		const gl = this._gl;
		// gl.bindFramebuffer(gl.FRAMEBUFFER, target);

		const batch = this.tileBatch;
		const vp = this._vp;
		mat4.mul(vp, camera.projectionMatrix, camera.invModelMatrix);

		// gl.disable(gl.BLEND);
		// gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

		// gl.enable(gl.POLYGON_OFFSET_FILL);
		// gl.polygonOffset(0, 0);

		// gl.clearStencil(0);
		// gl.clear(gl.STENCIL_BUFFER_BIT);
		// gl.enable(gl.STENCIL_TEST);
		// gl.stencilFunc(gl.ALWAYS, 1, 0xff);
		// gl.stencilOp(gl.REPLACE, gl.REPLACE, gl.REPLACE);
		// gl.stencilMask(0xff);

		// Clear with white;
		gl.clearColor(0.894, 0.894, 0.894, 1);
		gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
		// gl.viewport(0, 0, camera.viewportWidth, camera.viewportHeight);

		for (let i = 0, n = this.nodes.length; i < n; ++i)
			this.nodes[i].predraw(camera, vp, null);

		// this.nodes[0].draw(this.camera, vp, null);
		// this.nodes[1].draw(this.camera, vp, null);
		for (let i = 0, n = this.nodes.length; i < n; ++i)
			this.nodes[i].draw(camera, vp, null);

		if (!this.bumpBias && batch.bufferFill > 0) {
			// Shaders.
			gl.useProgram(this.textureProgram.program);
			gl.uniform1i(this.textureProgram.textureLocation, 0);
			gl.uniformMatrix4fv(this.textureProgram.mvpLocation,
													false, vp);

			// Vertex data.
			gl.bindBuffer(gl.ARRAY_BUFFER, batch.vertexBuffer);
			gl.bufferData(gl.ARRAY_BUFFER, batch.buffer, gl.STREAM_DRAW);
			gl.enableVertexAttribArray(0);
			gl.enableVertexAttribArray(1);
			gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 20, 0);
			gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 20, 12);

			// Textures.
			gl.activeTexture(gl.TEXTURE0);
			gl.bindTexture(gl.TEXTURE_2D, batch.atlas.texture);

			// Draw.
			gl.drawArrays(gl.TRIANGLES, 0, batch.bufferFill / 5);

			if (this._debugRendering) {
				// Vertex data.
				gl.bindBuffer(gl.ARRAY_BUFFER, this.linesVertexBuffer);
				gl.bufferData(gl.ARRAY_BUFFER, this.linesBuffer.buffer,
											gl.STREAM_DRAW);
				gl.enableVertexAttribArray(0);
				gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 12, 0);

				// Draw line grid for debugging.
				const color = new Float32Array(4);
				color[0] = 0;
				color[1] = 0;
				color[2] = 1;
				color[3] = 1;
				gl.useProgram(this.fillProgram.program);
				gl.uniform4fv(this.fillProgram.colorLocation, color);
				gl.uniformMatrix4fv(this.fillProgram.mvpLocation, false, vp);
				gl.lineWidth(1);
				gl.drawArrays(gl.LINES, 0, this.linesBuffer.fill / 3);
			}

			gl.polygonOffset(-1, -1);
			gl.stencilFunc(gl.ALWAYS, 0, 0xff);
			gl.colorMask(false, false, false, false);
			gl.depthMask(false);
			gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
			gl.useProgram(this.fillProgram.program);
			const color = new Float32Array(4);
			color[0] = 1;
			color[1] = 0;
			color[2] = 0;
			color[3] = 1;
			gl.uniform4fv(this.fillProgram.colorLocation, color);
			gl.uniformMatrix4fv(this.fillProgram.mvpLocation, false, vp);

			gl.stencilFunc(gl.EQUAL, 1, 0xff);
			gl.stencilMask(0);
			gl.colorMask(true, true, true, true);
			gl.depthMask(true);

			// Vertex Data.
			this.unitQuadVertexBuffer.bind();

			// Shaders.
			gl.depthMask(false);
			gl.useProgram(this.fillProgram.program);
			const p = mat4.frustum(mat4.create(), 0, camera.viewportWidth,
														camera.viewportHeight, 0, 1, 0);
			color[0] = 0;
			color[1] = 0;
			color[2] = 0;
			color[3] = 0.5;
			gl.uniform4fv(this.fillProgram.colorLocation, color);
			const oy = mat4.mul(mat4.create(), p,
													mat4.scale(mat4.create(),
																		mat4.translate(mat4.create(),
																										mat4.create(),
																										vec3.fromValues(0, 0, 0)),
																		vec3.fromValues(camera.viewportWidth,
																										camera.viewportHeight, 1)));
			gl.uniformMatrix4fv(this.fillProgram.mvpLocation, false, oy);

			gl.disable(gl.STENCIL_TEST);
			gl.depthMask(true);

			this._drawBadges(vp);
			this._drawHoverFlash(vp);
			this._drawFocus(vp);

			if (this._debugRendering && !target) {
				gl.disable(gl.BLEND);
				// Vertex Data.
				this.unitQuadVertexBuffer.bind();

				// Shaders.
				gl.useProgram(this.textureProgram.program);
				const p = mat4.frustum(mat4.create(), 0, camera.viewportWidth,
															camera.viewportHeight, 0, 1, 0);
				gl.uniform1i(this.textureProgram.textureLocation, 0);
				gl.uniformMatrix4fv(this.textureProgram.mvpLocation, false,
														mat4.mul(mat4.create(), p,
																		mat4.scale(mat4.create(),
																								mat4.translate(mat4.create(),
																															mat4.create(),
																															vec3.fromValues(0, 0, 0)),
																								vec3.fromValues(400, 400, 1))));

				// Textures.
				gl.activeTexture(gl.TEXTURE0);
				gl.bindTexture(gl.TEXTURE_2D, batch.atlas.texture);

				// Draw the tile atlas.
				gl.drawArrays(gl.TRIANGLES, 0, 6);

				gl.uniformMatrix4fv(this.textureProgram.mvpLocation, false,
														mat4.mul(mat4.create(), p,
																		mat4.scale(mat4.create(),
																								mat4.translate(mat4.create(),
																															mat4.create(),
																															vec3.fromValues(400, 0, 0)),
																								vec3.fromValues(400, 400, 1))));
				gl.bindTexture(gl.TEXTURE_2D, this._overlayTexture);
				gl.drawArrays(gl.TRIANGLES, 0, 6);

				gl.enable(gl.BLEND);
			}
		}
	}

	private _drawFocus(vp: mat4) {
		if (this.focusBuffer.fill === 0)
			return;

		const gl = this._gl;

		gl.enable(gl.BLEND);
		gl.disable(gl.DEPTH_TEST);
		gl.depthMask(false);
		gl.polygonOffset(-1, -1);

		// Shaders.
		gl.useProgram(this.coloredQuadBorderProgram.program);
		gl.uniformMatrix4fv(this.coloredQuadBorderProgram.mvpLocation, false, vp);
		const color = new Float32Array(4);
		color[0] = 0;
		color[1] = 0.78;
		color[2] = 0.85;
		color[3] = 0.2;
		gl.uniform4fv(this.coloredQuadBorderProgram.colorLocation, color);
		color[0] = 0;
		color[1] = 0.78;
		color[2] = 0.85;
		color[3] = 1;
		gl.uniform4fv(this.coloredQuadBorderProgram.borderColorLocation, color);

		gl.bindBuffer(gl.ARRAY_BUFFER, this.focusVertexBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, this.focusBuffer.buffer, gl.STREAM_DRAW);
		gl.enableVertexAttribArray(0);
		gl.enableVertexAttribArray(1);
		gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 20, 0);
		gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 20, 12);

		// Draw focus.
		gl.drawArrays(gl.TRIANGLES, 0, this.focusBuffer.fill / 5);
		gl.disable(gl.DEPTH_TEST);
		gl.depthMask(true);
		gl.disable(gl.BLEND);
	}

	private _drawBadges(vp: mat4) {
		if (!this._badgesTexture || this.badgeBuffer.fill === 0)
			return;

		const gl = this._gl;

		gl.enable(gl.BLEND);
		gl.disable(gl.DEPTH_TEST);
		gl.depthMask(false);
		gl.polygonOffset(-1, -1);

		// Textures.
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, this._badgesTexture);

		// Shaders.
		gl.useProgram(this.textureProgram.program);
		gl.uniformMatrix4fv(this.textureProgram.mvpLocation, false, vp);
		gl.uniform1i(this.textureProgram.textureLocation, 0);

		gl.bindBuffer(gl.ARRAY_BUFFER, this.badgeVertexBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, this.badgeBuffer.buffer, gl.STREAM_DRAW);
		gl.enableVertexAttribArray(0);
		gl.enableVertexAttribArray(1);
		gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 20, 0);
		gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 20, 12);

		// Draw hover flash.
		gl.drawArrays(gl.TRIANGLES, 0, this.badgeBuffer.fill / 5);
		gl.disable(gl.DEPTH_TEST);
		gl.depthMask(true);
		gl.disable(gl.BLEND);
	}

	private _drawHoverFlash(vp: mat4) {
		if (this.hoverBuffer.fill === 0)
			return;

		const gl = this._gl;

		gl.enable(gl.BLEND);
		gl.disable(gl.DEPTH_TEST);
		gl.depthMask(false);
		gl.polygonOffset(-1, -1);

		// Shaders.
		gl.useProgram(this.coloredQuadProgram.program);
		gl.uniformMatrix4fv(this.coloredQuadProgram.mvpLocation, false, vp);

		gl.bindBuffer(gl.ARRAY_BUFFER, this.hoverVertexBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, this.hoverBuffer.buffer, gl.STREAM_DRAW);
		gl.enableVertexAttribArray(0);
		gl.enableVertexAttribArray(1);
		gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 28, 0);
		gl.vertexAttribPointer(1, 4, gl.FLOAT, false, 28, 12);

		// Draw hover flash.
		gl.drawArrays(gl.TRIANGLES, 0, this.hoverBuffer.fill / 7);
		gl.disable(gl.DEPTH_TEST);
		gl.depthMask(true);
		gl.disable(gl.BLEND);
	}
}

export class FlyerView {
	onboundschange: (controller: FlyerView) => void;
	onitempop: (id: number) => void;
	onclip: (id: number) => void;
	onitemhover: (id: number) => void;
	onbegindrag: () => void;
	onenddrag: () => void;
	oninteraction: () => void;

	private _scene: Scene;
	private _controller: FlyerController;
	private _focusZoomed = true;
	private _dragging = false;

	constructor(private _ctx: WebGLRenderingContext) {
		this._scene = new Scene(this._ctx);

		this._controller = new FlyerController;
		this._controller.onclick = this._handleClick;
		this._controller.onhover = this._handleHover;
		this._controller.onzoompress = this._handleZoomPress;
		this._controller.onboundschange = this._handleBoundsChange;
		this._controller.onstatechange = this._handleStateChange;
	}

	get scene() {
		return this._scene;
	}

	get controller() {
		return this._controller;
	}

	get animating() {
		return timeline.running;
	}

	focus(item: FlyerItem, zoom: boolean, forceZoom: boolean) {
		const node = <FlyerPageNode>this._scene.nodes[0];
		if (!node)
			return;

		if (!item) {
			node.setFocus(null);
			return;
		}

		const itemRect = new Rect(item.left, -item.top,
															item.right - item.left,
															item.top - item.bottom);
		node.setFocus(itemRect);

		if (zoom || forceZoom) {
			this._zoomFocused(itemRect, false, forceZoom);
			if (forceZoom)
				this._focusZoomed = false;
		}
	}

	input(sample: Sample) {
		this._controller.update(sample, false);
	}

	setClippings(clippings: Clipping[]) {
		for (let i = 0, n = this._scene.nodes.length; i < n; ++i) {
			const page = <FlyerPageNode>this._scene.nodes[i];
			if (page instanceof FlyerPageNode)
				page.clearClippings();
		}

		for (const clipping of clippings) {
			for (let i = 0, n = this._scene.nodes.length; i < n; ++i) {
				const page = <FlyerPageNode>this._scene.nodes[i];
				if (!(page instanceof FlyerPageNode))
					continue;

				for (let j = 0, m = page.items.length; j < m; ++j) {
					const item = page.items[j];
					if (String(item.id) === clipping.item.id) {
						page.addClipping(clipping);
						break;
					}
				}
			}
		}
		this._scene.invalidate();
	}

	setClippedCoupons(coupons: number[]) {
		for (let i = 0, n = this._scene.nodes.length; i < n; ++i) {
			const page = <FlyerPageNode>this._scene.nodes[i];
			if (page instanceof FlyerPageNode)
				page.setClippedCoupons(coupons);
		}
		this._scene.invalidate();
	}

	init(flyer: Flyer, items: FlyerItem[], coupons: FlyerItemCoupon[], clippedCouponId: number[], clippings: Clipping[]) {
		this._scene.handleContextRestored();

		const tileImageLoader = new TextureLoader;

		const pages: SceneNode[] = [];
		for (let k = 1; k <= 1; ++k) {
			const pageWidth = flyer.width;
			const numPages = flyer.width / pageWidth;
			this._controller.scrollWidth = flyer.width;
			this._controller.scrollHeight = flyer.height;

			const baseY = flyer.height * k;

			for (let i = 0; i < numPages; ++i) {
				const sprite = new FlyerPageNode;
				sprite.tileImageLoader = tileImageLoader;
				sprite.contentLeft = pageWidth * i;
				sprite.contentTop = 0;
				sprite.contentWidth = pageWidth;
				sprite.contentHeight = flyer.height;
				sprite.flyer = flyer;
				sprite.scene = this._scene;

				const pageRect =
						new Rect(sprite.contentLeft, sprite.contentTop,
										sprite.contentWidth, sprite.contentHeight);
				const pageItems: PageItem[] = [];
				let pageCoupons: FlyerItemCoupon[] = [];

				for (let i = 0, n = items.length; i < n; ++i) {
					const item = items[i];
					const itemRect = new Rect(item.left, -item.top,
																		item.right - item.left,
																		item.top - item.bottom);
					if (Rect.intersects(pageRect, itemRect)) {
						pageItems.push({
							rect: itemRect,
							name: item.name,
							id: item.id
						});

						pageCoupons = pageCoupons.concat(coupons.filter((coupon) =>
								coupon.item.id === item.id));
					}
				}
				sprite.items = pageItems;
				sprite.coupons = pageCoupons;
				sprite.clippedCoupons = clippedCouponId;

				const ugh = mat4.scale(mat4.create(), mat4.create(),
						vec3.fromValues(pageWidth, -flyer.height, 1));
				const ugh2 = mat4.translate(mat4.create(), mat4.create(),
						vec3.fromValues(sprite.contentLeft, baseY, 0));
				mat4.mul(sprite.modelMatrix, ugh2, ugh);

				pages.push(sprite);
			}
		}

		pages.push(this._scene.debugCamera);
		pages.push(this._scene.camera);

		this._scene.nodes = pages;
		this.setClippings(clippings);
	}

	scaleDown() {
		let scale = this._controller.scale /
				this._controller.minimumScale;
		scale = Math.max(1, scale / 2);
		this._controller.setZoomScaleAnimated(
				this._controller.minimumScale * scale);
	}

	scaleUp() {
		let scale = this._controller.scale /
				this._controller.minimumScale;
		scale = Math.min(this._controller.maximumScale /
										this._controller.minimumScale, scale * 2);
		this._controller.setZoomScaleAnimated(
				this._controller.minimumScale * scale);
	}

	pageLeft() {
		this._controller.zoomToRect(
			this._controller.scrollLeft - this._controller.viewportWidth /
					this._controller.scale,
			this._controller.scrollTop,
			this._controller.viewportWidth / this._controller.scale,
			this._controller.viewportHeight / this._controller.scale,
			true);
	}

	pageRight() {
		this._controller.zoomToRect(
			this._controller.scrollLeft + this._controller.viewportWidth /
					this._controller.scale,
			this._controller.scrollTop,
			this._controller.viewportWidth / this._controller.scale,
			this._controller.viewportHeight / this._controller.scale,
			true);
	}

	private _handleZoomPress = () => {
		const node = <FlyerPageNode>this._scene.nodes[0];
		if (!node)
			return;

		const itemRect = node.getFocus();
		if (!itemRect)
			return;

		this._focusZoomed = !this._focusZoomed;
		this._zoomFocused(itemRect, true, false);
	}

	private _zoomFocused(itemRect: Rect, force: boolean, forceZoom: boolean) {
		const minimumScale = this._controller.minimumScale;
		const maximumScale = this._controller.maximumScale;

		if (!forceZoom && this._focusZoomed) {
			const scale = this._controller.scale;

			const viewportRect = new Rect(
				this._controller.scrollLeft,
				this._controller.scrollTop,
				this._controller.viewportWidth / scale,
				this._controller.viewportHeight / scale);
			if (!viewportRect.containsRect(itemRect) || force) {
				this._controller.zoomToRect(
					itemRect.left + itemRect.width * 0.5 -
							this._controller.viewportWidth * 0.5 / minimumScale,
					0, this._controller.viewportWidth / minimumScale,
					this._controller.scrollHeight, false);
			}
		} else {
			const factor = 1;
			if (itemRect.height * factor > this._controller.scrollHeight) {
				this._controller.zoomToRect(
						itemRect.left, itemRect.top,
						itemRect.width, itemRect.height, false);
			} else {
				const target = Math.min(
						this._controller.viewportWidth / itemRect.width * factor,
						this._controller.viewportHeight / itemRect.height * factor);
				if (target > maximumScale) {
					const target2 = Math.min(
						this._controller.viewportWidth / itemRect.width,
						this._controller.viewportHeight / itemRect.height);
					if (target2 > maximumScale) {
						this._controller.zoomToRect(
							itemRect.left + itemRect.width * 0.5 -
									this._controller.viewportWidth * 0.5 / maximumScale,
							itemRect.top + itemRect.height * 0.5 -
									this._controller.viewportHeight * 0.5 / maximumScale,
							this._controller.viewportWidth / maximumScale,
							this._controller.viewportHeight / maximumScale, false);
					} else {
						this._controller.zoomToRect(
							itemRect.left, itemRect.top,
							itemRect.width, itemRect.height, false);
					}
				} else {
					this._controller.zoomToRect(
						itemRect.left - (factor - 1) * 0.5 * itemRect.width,
						itemRect.top - (factor - 1) * 0.5 * itemRect.height,
						itemRect.width * factor,
						itemRect.height * factor, false);
				}
			}
		}
	}

	private _handleHover = (_: FlyerController, x: number, y: number) => {
		this._scene.hitTest(x, y, (item, node) => {
			node.addHover(item);
			if (this.onitemhover)
				this.onitemhover(item ? item.id : null);
		});
	}

	private _handleClick = (_: FlyerController, x: number, y: number) => {
		this._scene.hitTest(x, y, (item) => {
			if (!item)
				return;

			if (this.onitempop)
				this.onitempop(item.id);
		});
	}

	private _handleStateChange = () => {
		switch (this._controller.state) {
			case FlyerControllerState.DOUBLE_TAP_POINTER_DOWN:
			case FlyerControllerState.DRAGGING:
			case FlyerControllerState.PINCH_DRAGGING:
			case FlyerControllerState.POINTER_DOWN:
				if (this.oninteraction) this.oninteraction();
				break;
		}

		if (this._controller.state === FlyerControllerState.DRAGGING) {
			if (!this._dragging) {
				this._dragging = true;
				if (this.onbegindrag)
					this.onbegindrag();
			}
		} else if (this._dragging) {
			this._dragging = false;
			if (this.onenddrag)
				this.onenddrag();
		}
	}

	private _handleBoundsChange = () => {
		const scene = this._scene;
		const camera = scene.camera;
		if (!camera)
			return;

		const flyerController = this._controller;
		const y = -flyerController.scrollTop + flyerController.scrollHeight -
				flyerController.viewportHeight / flyerController.scale;
		const rect = new Rect(
			flyerController.scrollLeft, y,
			camera.viewportWidth / flyerController.scale,
			camera.viewportHeight / flyerController.scale);

		const near = 1;
		const far = 4000000000;

		mat4.perspective(camera.projectionMatrix, Math.PI / 2,
										camera.viewportWidth / camera.viewportHeight,
										near, far);

		mat4.scale(camera.modelMatrix,
							mat4.translate(mat4.create(), mat4.create(),
															vec3.fromValues(rect.left + rect.width / 2,
																							rect.top + rect.height / 2,
																							rect.height / 2)),
							vec3.fromValues(1, 1, 1));

		mat4.invert(camera.invModelMatrix, camera.modelMatrix);
		this._scene.invalidate();

		if (this.onboundschange)
			this.onboundschange(this);
	}
}
